import { useRef, useState } from 'react'
import { guessDeviceName, signUp } from '../lib/api'
import { useStore } from '../state/store'
import { Logo } from '../components/Logo'
import { languageOf, type LanguageCode } from '../lib/languages'
import { LEVEL_TIERS, scaleFor, tierFor } from '../lib/levels'

/**
 * The front door.
 *
 * There wasn't one. The app is usable without an account by design — it is
 * offline-first and your islands live on your device — and that design quietly
 * ate the entire sign-up path: an un-signed-in visitor fell straight through to
 * a working app, with a small "Sign in" in the rail and nothing anywhere that
 * created an account. `signUp` existed in the API client and on the server and
 * was called by no screen at all. Someone arriving from "Start seven days free"
 * landed in the product and could not find a way to buy it.
 *
 * So: one screen, one form, three ways forward. Create an account and the trial
 * starts immediately; sign in if you have one; or look around without either,
 * because breaking that would trade one problem for a worse one.
 */
export function WelcomeView({
  language,
  onSignIn,
  onLookAround,
}: {
  language: LanguageCode
  onSignIn: () => void
  onLookAround: () => void
}) {
  const lang = languageOf(language)
  const { data, dispatch } = useStore()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)

  /*
   * Asked here, once, because the answer changes what the very first screen
   * after this one looks like — and because by the time somebody has realised
   * their starter islands are too hard for them, they have already formed a
   * view of whether this product works.
   *
   * Starts on "just starting" rather than nothing selected. Someone paying for
   * a course in a language they cannot yet speak is more likely to be a
   * beginner than not, the chip is right here and visibly chosen rather than
   * assumed silently, and the wrong guess costs one tap in Settings. It is also
   * dispatched on submit whether or not it was touched, so what the form shows
   * is always what gets stored.
   */
  const [level, setLevel] = useState(LEVEL_TIERS[0].level)
  const [error, setError] = useState<string | null>(null)

  /*
   * Read at submit time, not only from state.
   *
   * A password manager fills these fields without always firing React's
   * onChange, so the component believes the box is empty while the browser
   * shows it full. Trusting state alone left the button disabled with the form
   * visibly complete and nothing explaining why — which is exactly how this was
   * first reported.
   */
  const emailRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  const tooShort = password.length > 0 && password.length < 12

  async function create() {
    if (busy) return

    const typedEmail = (emailRef.current?.value ?? email).trim()
    const typedPassword = passwordRef.current?.value ?? password

    /*
     * Validated here rather than by disabling the button. A disabled control
     * cannot say what is wrong with it, and the commonest reason for one to sit
     * dead is autofill the component never saw.
     */
    if (!typedEmail.includes('@')) {
      setError('That does not look like an email address.')
      emailRef.current?.focus()
      return
    }
    if (typedPassword.length < 12) {
      setError(`Passwords need twelve characters or more — that one is ${typedPassword.length}.`)
      passwordRef.current?.focus()
      return
    }

    setBusy(true)
    setError(null)

    const result = await signUp(typedEmail, typedPassword, guessDeviceName())
    if (!result.ok) {
      setBusy(false)
      setError(result.error)
      return
    }

    /*
     * After the sign-up succeeds, never before it.
     *
     * `updateSettings` stamps settingsUpdatedAt with the wall clock, and a
     * fresh store deliberately carries 0 there so that factory defaults can
     * never outrank settings synced from another device. Writing this before
     * the request meant a *failed* sign-up — an existing subscriber typing
     * their own email into the sign-up box and getting "an account already
     * exists" — left a brand-new browser holding DEFAULT_SETTINGS stamped
     * `now`. Signing in next resets lastPushedAt to 0 and pushes before it
     * pulls, so those defaults would win last-write-wins on the server and
     * wipe the account's real voice, speed, typing mode and tone settings on
     * every device they own.
     */
    dispatch({
      type: 'updateSettings',
      patch: {
        hskLevel: level,
        // Merged, not replaced: signing out keeps local work, so this screen
        // can be reached with levels already set for other languages.
        levelByLanguage: { ...data.settings.levelByLanguage, [language]: level },
      },
    })

    dispatch({
      type: 'patchSync',
      patch: {
        token: result.token,
        deviceId: result.deviceId,
        deviceName: result.deviceName ?? guessDeviceName(),
        accountEmail: result.user?.email ?? (emailRef.current?.value ?? email).trim(),
      },
    })

    // The whole app changes shape once a language is unlocked — the rail, the
    // language chips, the paywall. Reload rather than patching state in place.
    window.location.reload()
  }

  return (
    <div className="signin">
      <div className="signin-head">
        <Logo size={44} brand={lang.ui.brand} />
      </div>

      <h1 className="page-title" style={{ marginBottom: '0.3rem' }}>
        Seven days of {lang.name}, free
      </h1>
      <p className="lede" style={{ marginBottom: '1.4rem' }}>
        No card, nothing to cancel — it simply stops. Your islands, your practice history and your
        devices stay yours either way.
      </p>

      {error && (
        <div className="notice is-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div className="card">
        <div className="field">
          <span className="field-label">How much {lang.name} do you have?</span>
          <div className="toggle-group" style={{ marginTop: '0.15rem' }}>
            {LEVEL_TIERS.map((tier) => (
              <button
                key={tier.level}
                type="button"
                className={`btn is-small ${level === tier.level ? 'is-on' : ''}`}
                onClick={() => setLevel(tier.level)}
              >
                {tier.label}
              </button>
            ))}
          </div>
          {/* A <p>, not a <span>: .toggle-group is inline-flex, so an inline
              sibling wraps around the chips instead of sitting under them. */}
          <p style={{ fontSize: '0.84rem', color: 'var(--text-dim)', margin: '0.45rem 0 0' }}>
            {scaleFor(lang.code, tierFor(level))} — {tierFor(level).blurb} Change it any time in
            Settings; it decides which islands come first, and hides none of them.
          </p>
        </div>

        <label className="field">
          <span className="field-label">Email</span>
          <input
            className="input"
            type="email"
            autoComplete="email"
            ref={emailRef}
            autoFocus
            value={email}
            placeholder="someone@example.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field-label">Choose a password</span>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            ref={passwordRef}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void create()
            }}
          />
          {/* Amber while it is short, so the requirement is visible at a glance
              rather than a grey line somebody reads only after being stuck. */}
          <span
            style={{
              fontSize: '0.84rem',
              color: tooShort ? 'var(--amber)' : 'var(--text-dim)',
            }}
          >
            {tooShort
              ? `${12 - password.length} more character${12 - password.length === 1 ? '' : 's'} needed — a short sentence beats something clever.`
              : 'Twelve characters or more. A short sentence you will remember is ideal.'}
          </span>
        </label>

        <button
          type="button"
          className="btn is-primary"
          disabled={busy}
          onClick={() => void create()}
        >
          {busy ? 'Setting it up…' : `Start my free week of ${lang.name}`}
        </button>

        <p className="muted" style={{ fontSize: '0.84rem', margin: '0.8rem 0 0' }}>
          ${'19.95'} a month afterwards, for {lang.name} alone. Each language is separate.
        </p>
      </div>

      <div className="row" style={{ marginTop: '1rem', gap: '0.6rem', flexWrap: 'wrap' }}>
        <button type="button" className="btn is-small" onClick={onSignIn}>
          I already have an account
        </button>
        {/* Kept deliberately. The app works without an account and always has;
            removing that to funnel people would trade one problem for a worse
            one. It is quieter than the two above, not hidden. */}
        <button type="button" className="btn is-small is-quiet" onClick={onLookAround}>
          Look around first
        </button>
      </div>
    </div>
  )
}
